/**
 * Map Create/Edit Trip partner select value to API ownerType + partnerId.
 *
 * Rule: Partner field is always required in the UI (Bubakii or a third-party partner).
 * - Bubakii → ownerType bubakii, partnerId null
 * - Partner id → ownerType third_party, partnerId set
 *
 * Vehicle / Driver dropdowns are scoped to the selected partner and only shown
 * when that partner has matching assets.
 *
 * Capacity vs truck (capacity-first, truck optional — not XOR):
 * - Open shared load: capacityClass required; vehicle optional
 * - Schedule trip: vehicle required when partner has fleet; capacity auto-filled
 *   from vehicle and still editable; if zero vehicles, capacity required + warning
 */

import {
  BUBAKII_PROVIDER,
  BUBAKII_PROVIDER_ID,
  driverFiltersForProvider,
  vehicleFiltersForProvider,
} from "../../drivers/utils/providerDriverAssignment";
import {
  ACCEPTING_LOAD_TRIP_STATUS,
  PLANNED_TRIP_STATUS,
} from "../constants/tripStatuses.js";

/** Fallback sizes mirroring System Setting pricing.vehicles.bands seed. */
export const FALLBACK_CAPACITY_CLASS_OPTIONS = Object.freeze([
  { value: "bakkie", label: "Bakkie" },
  { value: "1_ton", label: "1t" },
  { value: "4_ton", label: "4t" },
  { value: "8_ton", label: "8t" },
  { value: "16_ton", label: "16t" },
]);

const VEHICLE_TYPE_TO_CAPACITY_CLASS = Object.freeze({
  small: "1_ton",
  medium: "4_ton",
  large: "8_ton",
});

/**
 * @param {string|undefined|null} partnerSelectionId - Form partner select value
 * @returns {{ ownerType: "bubakii"|"third_party", partnerId: string|null }}
 */
export function partnerSelectionToOperator(partnerSelectionId) {
  if (!partnerSelectionId || partnerSelectionId === BUBAKII_PROVIDER_ID) {
    return { ownerType: "bubakii", partnerId: null };
  }
  return { ownerType: "third_party", partnerId: partnerSelectionId };
}

/**
 * Map stored trip operator fields back to the partner select value.
 * @param {{ ownerType?: string, partnerId?: string|null }} trip
 * @returns {string|undefined}
 */
export function operatorToPartnerSelection(trip) {
  if (!trip) return undefined;
  if (trip.partnerId) return trip.partnerId;
  if (trip.ownerType === "bubakii") return BUBAKII_PROVIDER_ID;
  return undefined;
}

/**
 * Resolve provider object from partner select value for asset filters.
 * @param {string|undefined|null} partnerSelectionId
 * @returns {Object|null}
 */
export function partnerSelectionToProvider(partnerSelectionId) {
  if (!partnerSelectionId) return null;
  if (partnerSelectionId === BUBAKII_PROVIDER_ID) {
    return { ...BUBAKII_PROVIDER };
  }
  return { id: partnerSelectionId, ownerType: "third_party" };
}

/**
 * Vehicle list filters for the selected partner (create: available only).
 * @param {string|undefined|null} partnerSelectionId
 * @param {{ status?: string }} [options]
 * @returns {Object|null}
 */
export function vehicleFiltersForPartnerSelection(
  partnerSelectionId,
  options = { status: "available" }
) {
  return vehicleFiltersForProvider(
    partnerSelectionToProvider(partnerSelectionId),
    options
  );
}

/**
 * Driver list filters for the selected partner.
 * @param {string|undefined|null} partnerSelectionId
 * @returns {Object|null}
 */
export function driverFiltersForPartnerSelection(partnerSelectionId) {
  return driverFiltersForProvider(
    partnerSelectionToProvider(partnerSelectionId)
  );
}

/**
 * Whether the Vehicle field should render (partner selected + has vehicles).
 * @param {string|undefined|null} partnerSelectionId
 * @param {Array} vehicles
 * @param {boolean} [loading]
 * @returns {boolean}
 */
export function shouldShowVehicleField(
  partnerSelectionId,
  vehicles = [],
  loading = false
) {
  if (!partnerSelectionId || loading) return false;
  return (vehicles?.length || 0) > 0;
}

/**
 * Whether the Driver field should render (partner selected + has drivers).
 * @param {string|undefined|null} partnerSelectionId
 * @param {Array} drivers
 * @param {boolean} [loading]
 * @returns {boolean}
 */
export function shouldShowDriverField(
  partnerSelectionId,
  drivers = [],
  loading = false
) {
  if (!partnerSelectionId || loading) return false;
  return (drivers?.length || 0) > 0;
}

/**
 * Partner has no vehicles after assets loaded.
 * @param {string|undefined|null} partnerSelectionId
 * @param {Array} vehicles
 * @param {boolean} [loading]
 * @returns {boolean}
 */
export function partnerHasNoVehicles(
  partnerSelectionId,
  vehicles = [],
  loading = false
) {
  if (!partnerSelectionId || loading) return false;
  return (vehicles?.length || 0) === 0;
}

/**
 * Vehicle is required for Schedule trip when the partner has fleet options.
 * Open shared load never requires a vehicle.
 * @param {string} tripIntent
 * @param {boolean} hasVehicles
 * @returns {boolean}
 */
export function isVehicleRequiredForIntent(tripIntent, hasVehicles) {
  return tripIntent === PLANNED_TRIP_STATUS && Boolean(hasVehicles);
}

/**
 * Capacity class is always required on create (shared matching + schedule).
 * @returns {boolean}
 */
export function isCapacityClassRequired() {
  return true;
}

/**
 * Whether create/update submit should be blocked solely because fleet is empty.
 * Open shared load: never. Schedule: never (capacity-only allowed with warning).
 * @returns {boolean}
 */
export function shouldBlockSubmitForMissingFleet() {
  return false;
}

/**
 * @param {unknown} value
 * @returns {string|null}
 */
export function normalizeCapacityClass(value) {
  if (value == null) return null;
  const trimmed = String(value).trim().toLowerCase().replace(/\s+/g, "_");
  if (!trimmed) return null;
  const shorthand = trimmed.match(/^(\d+)t$/);
  if (shorthand) return `${shorthand[1]}_ton`;
  return trimmed;
}

/**
 * @param {string|null|undefined} capacityClass
 * @param {{ label?: string }|null} [band]
 * @returns {string}
 */
export function formatCapacityClassLabel(capacityClass, band = null) {
  if (band?.label) return String(band.label);
  const size = normalizeCapacityClass(capacityClass);
  if (!size) return "";
  const tonMatch = size.match(/^(\d+)_ton$/);
  if (tonMatch) return `${tonMatch[1]}t`;
  if (size === "bakkie") return "Bakkie";
  return size.replace(/_/g, " ");
}

/**
 * Build capacity chip options from pricing.vehicles.bands or fallback.
 * @param {unknown} [bands]
 * @returns {Array<{ value: string, label: string }>}
 */
export function resolveCapacityClassOptions(bands) {
  if (Array.isArray(bands) && bands.length > 0) {
    const options = bands
      .map((band) => {
        const value = normalizeCapacityClass(band?.size || band?.id);
        if (!value) return null;
        return {
          value,
          label: formatCapacityClassLabel(value, band),
        };
      })
      .filter(Boolean);
    if (options.length > 0) return options;
  }
  return [...FALLBACK_CAPACITY_CLASS_OPTIONS];
}

/**
 * @param {number} capacityKg
 * @param {string[]} [sizes]
 * @returns {string|null}
 */
export function capacityClassFromKg(
  capacityKg,
  sizes = FALLBACK_CAPACITY_CLASS_OPTIONS.map((o) => o.value)
) {
  const kg = Number(capacityKg);
  if (!Number.isFinite(kg) || kg <= 0) return null;

  const tonCandidates = sizes
    .map((size) => {
      const match = String(size).match(/^(\d+)_ton/);
      return match ? { size, tons: Number(match[1]) } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.tons - b.tons);

  if (tonCandidates.length === 0) return null;

  const targetTons = kg / 1000;
  const fitting = tonCandidates.find((c) => c.tons >= targetTons);
  return (fitting || tonCandidates[tonCandidates.length - 1]).size;
}

/**
 * Derive capacityClass when a vehicle is selected (editable afterward).
 * @param {object|null|undefined} vehicle
 * @param {Array<{ value: string }>} [options]
 * @returns {string|null}
 */
export function deriveCapacityClassFromVehicle(
  vehicle,
  options = FALLBACK_CAPACITY_CLASS_OPTIONS
) {
  if (!vehicle) return null;
  const sizes = options.map((o) => o.value);

  if (vehicle.capacityClass) {
    const fromField = normalizeCapacityClass(vehicle.capacityClass);
    if (fromField) return fromField;
  }

  const fromKg = capacityClassFromKg(vehicle.capacity, sizes);
  if (fromKg) return fromKg;

  const typeKey = String(vehicle.vehicleType || "")
    .trim()
    .toLowerCase();
  const fromType = VEHICLE_TYPE_TO_CAPACITY_CLASS[typeKey] || null;
  if (fromType && sizes.includes(fromType)) return fromType;
  return fromType;
}

/**
 * Warning copy when scheduling with no truck on the partner.
 * @param {string} tripIntent
 * @param {boolean} noVehicles
 * @returns {string|null}
 */
export function noFleetScheduleWarning(tripIntent, noVehicles) {
  if (!noVehicles) return null;
  if (tripIntent === ACCEPTING_LOAD_TRIP_STATUS) {
    return "No vehicles for this partner — capacity class is still required; truck can be assigned later.";
  }
  return "No truck assigned — this partner has no vehicles. Set capacity class to schedule without a plate, or add a vehicle first.";
}

export { ACCEPTING_LOAD_TRIP_STATUS, PLANNED_TRIP_STATUS };
